import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "../lib/api";
import ImageUpload from "../components/ImageUpload";

const FIELDS = [
  { key: "shop_name", label: "Shop name (on receipts)", placeholder: "AMRAIYA BOUTIQUE" },
  { key: "address", label: "Address", placeholder: "14 Kala Ghoda, Mumbai 400001", multi: true },
  { key: "phone", label: "Phone", placeholder: "+91 22 4000 1200" },
  { key: "gstin", label: "Shop GSTIN (shown on every bill)", placeholder: "27ABCDE1234F1Z5" },
  { key: "footer_line", label: "Receipt footer line", placeholder: "Thank you for shopping with us" },
];

export default function PosSettings() {
  const [s, setS] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/pos/settings").then((r) => setS(r.data || {})); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/pos/settings", s); setS(data); toast.success("Saved"); }
    catch (err) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggleStorefront = async () => {
    const next = !s.storefront_offline;
    const payload = { ...s, storefront_offline: next };
    setS(payload);
    try {
      const { data } = await api.put("/pos/settings", payload);
      setS(data);
      toast.success(next ? "Storefront is now offline — POS & Admin keep working" : "Storefront is back online");
    } catch { toast.error("Could not update storefront status"); }
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="pos-settings-page">
      <h1 className="text-2xl font-semibold text-slate-100 mb-1">Settings</h1>
      <p className="text-sm text-slate-500 mb-6">Shop information that appears on printed receipts.</p>

      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mb-6" data-testid="storefront-toggle-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-slate-100 font-semibold">Online storefront</h2>
            <p className="text-xs text-slate-500 mt-1">Turn off to take the public website offline (visitors see a "back soon" page). POS &amp; Admin keep working.</p>
          </div>
          <button
            onClick={toggleStorefront}
            className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${s.storefront_offline ? "bg-red-500/80" : "bg-emerald-500/80"}`}
            data-testid="storefront-toggle"
            title={s.storefront_offline ? "Storefront offline — click to bring online" : "Storefront online — click to take offline"}
          >
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${s.storefront_offline ? "left-8" : "left-1"}`} />
          </button>
        </div>
        <p className={`text-xs mt-2 font-medium ${s.storefront_offline ? "text-red-400" : "text-emerald-400"}`} data-testid="storefront-state">
          {s.storefront_offline ? "Offline — visitors see the maintenance page" : "Online — storefront is live"}
        </p>
        {s.storefront_offline && (
          <label className="block mt-4">
            <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Maintenance message (optional)</span>
            <textarea rows={2} value={s.storefront_message || ""} onChange={(e) => setS({ ...s, storefront_message: e.target.value })} placeholder="We're refreshing our collection — back soon!" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 resize-none" data-testid="settings-storefront-message" />
          </label>
        )}
      </div>

      <div className="space-y-4 bg-slate-900 border border-slate-800 rounded-lg p-6">
        <div className="pb-2">
          <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Store logo (top of every receipt)</span>
          <div className="bg-slate-950 rounded p-3" data-testid="settings-logo">
            <ImageUpload label="" value={s.logo_url || ""} onChange={(url) => setS({ ...s, logo_url: url })} />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">PNG/JPG. Leave empty to use the default Amraiya wordmark.</p>
        </div>
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">{f.label}</span>
            {f.multi ? (
              <textarea rows={2} value={s[f.key] || ""} onChange={(e) => setS({ ...s, [f.key]: e.target.value })} placeholder={f.placeholder} className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 resize-none" data-testid={`settings-${f.key}`} />
            ) : (
              <input value={s[f.key] || ""} onChange={(e) => setS({ ...s, [f.key]: e.target.value })} placeholder={f.placeholder} className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200" data-testid={`settings-${f.key}`} />
            )}
          </label>
        ))}
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-medium text-sm disabled:opacity-50" data-testid="settings-save">
          <Save size={14} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}
